import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { api } from "../../api/client";
import { AppDialog } from "../../components/AppDialog";
import { AppDataGrid, type AppColumn } from "../../components/AppDataGrid";
import { useNotify } from "../../components/notifications";
import { useConfirm, useSaveFlow } from "../../components/confirm";
import { BusyIcon } from "../../components/loading";
import { fmtLocalShort } from "../../utils/datetime";
import type { SqlEditorHandle } from "../queryConsole/SqlEditorCM";

// CodeMirror is heavy — lazy-load it into its own chunk (same as SqlQueryConsolePage).
const SqlEditor = lazy(() => import("../queryConsole/SqlEditorCM"));

/**
 * Settings ▸ EDD ▸ EDD Builder → Query Details → the legacy **"EDDQuery Builder"**
 * popup (ALPACA EDDQueryBuilder_DetailView_SheetEDD). A tabbed editor for ONE sheet
 * of an EDD template:
 *  - header: Query Name* · Sheet Name*
 *  - **Query Builder** tab: a raw-SQL editor + **Execute** (runs it verbatim via
 *    /api/v1/edd-queries/execute → legacy EDD_ExecuteQuery_SP) + a preview grid.
 *  - **Field Editor** tab: the per-column output settings, AUTO-SYNCED from the
 *    Execute result columns (add new, drop missing), batch-editable, with a
 *    "copy the first row's value down the column" helper per editable column.
 *  OK saves the query + its Field Editor rows in one transaction (the writer
 *  reconciles dbo.EDDFieldEditor); Cancel discards.
 */
interface EddField {
  oid: string | null;
  fieldName: string;
  caption: string;
  width: number;
  sort: number;
  visible: boolean;
  frozen: boolean;
  isRequired: boolean;
  dateTimeNeed: boolean;
}
interface PreviewCol { name: string; type: string; isDateTime: boolean; }
interface Preview { columns: PreviewCol[]; rows: (string | null)[][]; }
type PreviewRow = Record<string, string | null>;

interface Props {
  /** Query Oid for an existing sheet, or null for a new one. */
  queryOid: string | null;
  /** The clicked row's full record (listReturnsFullRecord) — queryName / sheetName / queryBuilder. */
  prefill?: Record<string, unknown>;
  /** The owning EDD Builder's Oid. Null = the EDD is not saved yet (the FK can't be set). */
  eddBuilderOid: string | null;
  /** Existing sheet count — seeds the "Sheet{n}" default for a new sheet. */
  existingCount: number;
  /**
   * When the EDD Builder is NOT saved yet (no FK), buffer the query instead of
   * POSTing an orphan — the parent's Save writes it (legacy adds queries to a new
   * EDD in memory, no "save first" warning). When set, it REPLACES the POST path.
   */
  onCapture?: (payload: Record<string, unknown>) => void;
  onClose: () => void;
  onSaved: () => void;
}

export function EddQueryBuilderDialog({ queryOid, prefill, eddBuilderOid, existingCount, onCapture, onClose, onSaved }: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const confirm = useConfirm();
  const editorRef = useRef<SqlEditorHandle>(null);
  const isNew = queryOid == null;

  const [queryName, setQueryName] = useState(() => String(prefill?.queryName ?? ""));
  const [sheetName, setSheetName] = useState(() => String(prefill?.sheetName ?? (isNew ? `Sheet${existingCount + 1}` : "")));
  const [sql, setSql] = useState(() => String(prefill?.queryBuilder ?? ""));
  // Seed from prefill.fields so re-opening a BUFFERED (not-yet-saved) query keeps its
  // Field Editor rows; a saved query reloads them from the server in the effect below.
  const [fields, setFields] = useState<EddField[]>(() => prefillFields(prefill));
  const [preview, setPreview] = useState<Preview | null>(null);
  const [tab, setTab] = useState<"query" | "fields">("query");
  const [executing, setExecuting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [snapshot, setSnapshot] = useState<EddField[]>(() => prefillFields(prefill));

  // Load the saved Field Editor rows for an existing (server) query. A buffered
  // query being re-edited (queryOid "pending-<i>") has no server record — its
  // fields are seeded from prefill.fields above, so skip the fetch.
  useEffect(() => {
    if (!queryOid || queryOid.startsWith("pending-")) return;
    let alive = true;
    api.get<{ data?: EddField[] }>(`/api/v1/edd-queries/${queryOid}/fields`)
      .then((r) => {
        if (!alive) return;
        const loaded = (r.data?.data ?? []).map((f) => ({ ...f, fieldName: f.fieldName ?? "", caption: f.caption ?? "" }));
        setFields(loaded);
        setSnapshot(loaded);
      })
      .catch(() => { if (alive) notify.error("Could not load the Field Editor rows."); });
    return () => { alive = false; };
  }, [queryOid, notify]);

  // Reconcile the field set with the Execute result columns (legacy auto-sync,
  // EDDBuilderViewController :2619-2659): keep a column's existing settings, add a
  // new row for a new column, drop a row whose column is no longer returned.
  const reconcile = (cols: PreviewCol[], prev: EddField[]): EddField[] => {
    const byName = new Map(prev.map((f) => [f.fieldName, f]));
    return cols.map((c, i) =>
      byName.get(c.name) ?? {
        oid: null, fieldName: c.name, caption: c.name, width: 400, sort: i,
        visible: true, frozen: false, isRequired: false, dateTimeNeed: c.isDateTime,
      });
  };

  const execute = async () => {
    if (!sql.trim()) { notify.warning("Enter a query to execute."); return; }
    if (executing) return;
    setExecuting(true);
    try {
      const res = await api.post<{ data?: Preview }>("/api/v1/edd-queries/execute", { sql });
      const data = res.data?.data ?? { columns: [], rows: [] };
      setPreview(data);
      const next = reconcile(data.columns, fields);
      setFields(next);
      setSnapshot(next);
    } catch (e: unknown) {
      notify.error(apiErr(e, "Execute failed."));
    } finally {
      setExecuting(false);
    }
  };

  const doSave = () => {
    if (!queryName.trim()) { notify.warning("Query Name must not be empty."); setTab("query"); return; }
    const fieldRows = fields.map((f) => ({
      oid: f.oid, fieldName: f.fieldName, caption: f.caption, width: f.width, sort: f.sort,
      visible: f.visible, frozen: f.frozen, isRequired: f.isRequired, dateTimeNeed: f.dateTimeNeed,
    }));
    // The EDD Builder isn't saved yet (no FK) → BUFFER the query; the parent's Save
    // writes it. Legacy adds queries to a new EDD in memory with no warning (owner
    // request 2026-10-22). The eddBuilder FK is stamped by the parent on write.
    if (!eddBuilderOid) {
      if (onCapture) {
        onCapture({ queryName: queryName.trim(), sheetName: sheetName.trim(), queryBuilder: sql, fields: fieldRows });
        // Status popup (owner request 2026-10-22): the query is buffered now and
        // persists when the EDD Builder itself is saved. Close after acknowledgement.
        void confirm.alert({
          tone: "success",
          title: isNew ? "Query Added" : "Query Updated",
          message: `Query ${isNew ? "added" : "updated"}. It will be saved when you save the EDD Builder.`,
        }).then(onSaved);
      } else {
        notify.warning("Save the EDD Builder first, then add its queries.");
      }
      return;
    }
    const payload = {
      queryName: queryName.trim(),
      sheetName: sheetName.trim(),
      queryBuilder: sql,
      eddBuilder: eddBuilderOid,
      fields: fieldRows,
    };
    void runSave({
      formName: "EDD Builder",
      auditAction: isNew ? "create" : "edit",
      save: async () => {
        if (isNew) await api.post("/api/v1/edd-queries", payload);
        else await api.put(`/api/v1/edd-queries/${queryOid}`, payload);
      },
      successMessage: "Query saved successfully.",
      onSaved,
    });
  };

  const patchField = (idx: number, patch: Partial<EddField>) => {
    setFields((prev) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
    // Editing any value in a row auto-checks that row (owner request 2026-10-22, image 756).
    setSelected((prev) => {
      const f = fields[idx];
      if (!f) return prev;
      const key = f.oid ?? `new-${idx}-${f.fieldName}`;
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  // Field Editor row-selection column (header = select-all), keyed by the row's stable key.
  const rowKey = (f: EddField, i: number) => f.oid ?? `new-${i}-${f.fieldName}`;
  const fieldKeys = fields.map(rowKey);
  const selCount = fieldKeys.reduce((n, k) => n + (selected.has(k) ? 1 : 0), 0);
  const allChecked = fields.length > 0 && selCount === fields.length;
  const someChecked = selCount > 0 && selCount < fields.length;
  // Field Editor local batch-edit dirty flag (legacy "Save changes / Cancel changes").
  const dirty = JSON.stringify(fields) !== JSON.stringify(snapshot);
  const toggleRow = (k: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(k); else next.delete(k);
      return next;
    });

  // Preview grid — dynamic columns from the result schema.
  const prevCols: AppColumn<PreviewRow>[] = (preview?.columns ?? []).map((c, i) => ({
    field: `c${i}`,
    header: c.name,
    width: 160,
    text: (r) => {
      const v = r[`c${i}`];
      return v != null && c.isDateTime ? fmtLocalShort(v) : (v ?? "");
    },
  }));
  const prevRows: PreviewRow[] = (preview?.rows ?? []).map((r, ri) => {
    const o: PreviewRow = { __id: String(ri) };
    r.forEach((v, i) => { o[`c${i}`] = v; });
    return o;
  });

  return (
    <AppDialog
      open
      title="EDDQuery Builder"
      icon="ti-table"
      width={1040}
      onClose={onClose}
      footer={<>
        <button className="ca-btn ca-btn-solid" onClick={doSave}>OK</button>
        <button className="ca-btn" onClick={onClose}>Cancel</button>
      </>}
    >
      <style>{EQB_CSS}</style>
      <div className="eqb">
        <div className="eqb-hdr">
          <label className="eqb-row">
            <span className="eqb-lbl">Query Name:<span className="eqb-star">*</span></span>
            <input className="eqb-in" value={queryName} maxLength={200} onChange={(e) => setQueryName(e.target.value)} />
          </label>
          <label className="eqb-row">
            <span className="eqb-lbl">Sheet Name:<span className="eqb-star">*</span></span>
            <input className="eqb-in" value={sheetName} maxLength={200} onChange={(e) => setSheetName(e.target.value)} />
          </label>
        </div>

        <div className="eqb-tabctl">
        <div className="eqb-tabs">
          <button type="button" className={`eqb-tab${tab === "query" ? " active" : ""}`} onClick={() => setTab("query")}>Query Builder</button>
          <button type="button" className={`eqb-tab${tab === "fields" ? " active" : ""}`} onClick={() => setTab("fields")}>
            Field Editor <span className="eqb-tab-count">{fields.length}</span>
          </button>
        </div>

        {tab === "query" ? (
          <div className="eqb-qtab">
            <div className="eqb-qbar">
              <button className="ca-btn ca-btn-solid" onClick={execute} disabled={executing}>
                <BusyIcon busy={executing} icon="ti-player-play" /> {executing ? "Executing…" : "Execute"}
              </button>
            </div>
            <div className="eqb-editor">
              <Suspense fallback={<textarea className="eqb-sql-fallback" value={sql} onChange={(e) => setSql(e.target.value)} spellCheck={false} />}>
                <SqlEditor ref={editorRef} value={sql} onChange={setSql} onExecute={execute} schema={{}} />
              </Suspense>
            </div>
            <div className="eqb-preview">
              {preview ? (
                <AppDataGrid<PreviewRow>
                  rows={prevRows}
                  columns={prevCols}
                  getRowId={(r) => r.__id ?? ""}
                  emptyText="No data to display"
                  hideHeaderTools
                />
              ) : (
                /* Empty grid shell (header strip + "No data to display") before Execute —
                   matching the legacy Query Builder preview (owner report 2026-10-22, image 754). */
                <div className="eqb-grid-shell">
                  <div className="eqb-grid-shell-hdr" />
                  <div className="eqb-grid-shell-body">No data to display</div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="eqb-ftab">
            {/* The grid (column headers) ALWAYS renders — even before Execute, when it
                shows "No data to display" in the body — matching the legacy Field Editor
                (owner report 2026-10-22, image 752). */}
            <div className="eqb-ftbl-scroll">
              <table className="eqb-ftbl">
                <thead>
                  <tr>
                    <th className="eqb-c eqb-cbcol">
                      <input
                        type="checkbox"
                        ref={(el) => { if (el) el.indeterminate = someChecked; }}
                        checked={allChecked}
                        disabled={fields.length === 0}
                        onChange={(e) => setSelected(e.target.checked ? new Set(fieldKeys) : new Set())}
                        title="Select all"
                      />
                    </th>
                    <th>FieldName</th>
                    <th>Caption</th>
                    <th>Width</th>
                    <th>Sort</th>
                    <th className="eqb-c">Visible</th>
                    <th className="eqb-c">DateTimeNeed</th>
                    <th className="eqb-c">Frozen</th>
                    <th className="eqb-c">IsRequired</th>
                  </tr>
                </thead>
                <tbody>
                  {fields.length === 0 ? (
                    <tr><td colSpan={9} className="eqb-empty-row">No data to display</td></tr>
                  ) : (
                    fields.map((f, i) => {
                      const key = rowKey(f, i);
                      return (
                      <tr key={key}>
                        <td className="eqb-c eqb-cbcol">
                          <input type="checkbox" checked={selected.has(key)} onChange={(e) => toggleRow(key, e.target.checked)} />
                        </td>
                        <td className="eqb-fname" title={f.fieldName}>{f.fieldName}</td>
                        <td><input className="eqb-cell" value={f.caption} onChange={(e) => patchField(i, { caption: e.target.value })} /></td>
                        <td><input className="eqb-cell eqb-num" inputMode="numeric" value={f.width} onChange={(e) => patchField(i, { width: Number(e.target.value) || 0 })} /></td>
                        <td><input className="eqb-cell eqb-num" inputMode="numeric" value={f.sort} onChange={(e) => patchField(i, { sort: Number(e.target.value) || 0 })} /></td>
                        <td className="eqb-c"><input type="checkbox" checked={f.visible} onChange={(e) => patchField(i, { visible: e.target.checked })} /></td>
                        <td className="eqb-c"><input type="checkbox" checked={f.dateTimeNeed} onChange={(e) => patchField(i, { dateTimeNeed: e.target.checked })} /></td>
                        <td className="eqb-c"><input type="checkbox" checked={f.frozen} onChange={(e) => patchField(i, { frozen: e.target.checked })} /></td>
                        <td className="eqb-c"><input type="checkbox" checked={f.isRequired} onChange={(e) => patchField(i, { isRequired: e.target.checked })} /></td>
                      </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            {/* Legacy inline batch-edit footer: commit / revert the field edits locally
                (the dialog's OK still persists to the DB). Owner report 2026-10-22, image 756. */}
            <div className="eqb-fbar">
              <button type="button" className="ca-btn ca-btn-solid" disabled={!dirty} onClick={() => setSnapshot(fields)}>
                <i className="ti ti-device-floppy" /> Save changes
              </button>
              <button type="button" className="ca-btn" disabled={!dirty} onClick={() => { setFields(snapshot); setSelected(new Set()); }}>
                <i className="ti ti-arrow-back-up" /> Cancel changes
              </button>
            </div>
          </div>
        )}
        </div>
      </div>
    </AppDialog>
  );
}


// A buffered (not-yet-saved) query carries its Field Editor rows on prefill.fields;
// a saved query loads them from the server instead. Returns [] for a fresh new query.
function prefillFields(prefill?: Record<string, unknown>): EddField[] {
  const f = prefill?.fields;
  return Array.isArray(f) ? (f as EddField[]) : [];
}

function apiErr(e: unknown, fallback: string): string {
  const r = (e as { response?: { data?: { error?: { message?: string } } } })?.response;
  return r?.data?.error?.message ?? (e instanceof Error ? e.message : fallback);
}

const EQB_CSS = `
  .eqb { display: flex; flex-direction: column; gap: 10px; min-height: 520px; }
  .eqb-hdr { display: flex; flex-direction: column; gap: 6px; }
  .eqb-row { display: flex; align-items: center; gap: 10px; }
  .eqb-lbl { width: 100px; flex: 0 0 100px; font-size: 13px; color: var(--ai-ink, #1f2430); }
  .eqb-star { color: var(--ai-red, #c0392b); margin-left: 2px; }
  .eqb-in { flex: 1 1 auto; min-height: var(--ai-control-h, 26px); padding: var(--ai-control-pad-y, 4px) 10px;
    border: 1px solid var(--ai-border, #d4d8e0); border-radius: 5px; font-size: 13px; box-sizing: border-box; }
  .eqb-tabctl { border: 1px solid var(--ai-line, #e3e6ec); border-radius: 6px; background: #fff; }
  .eqb-tabctl .eqb-qtab, .eqb-tabctl .eqb-ftab { padding: 10px; box-sizing: border-box; }
  .eqb-tabs { display: flex; gap: 2px; padding: 0 6px; border-bottom: 1px solid var(--ai-line, #e3e6ec); }
  .eqb-tab { appearance: none; border: 1px solid transparent; border-bottom: none; background: transparent;
    padding: 7px 16px; font-size: 13px; font-weight: 600; color: var(--ai-muted, #6b7280); cursor: pointer;
    border-radius: 6px 6px 0 0; margin-bottom: -1px; }
  .eqb-tab:hover { color: var(--ai-ink, #1f2430); }
  .eqb-tab.active { color: var(--ai-orange, #e8670a); border-color: var(--ai-line, #e3e6ec); background: #fff; }
  .eqb-tab-count { font-size: 11px; font-weight: 700; background: var(--ai-row-hover, #f1f3f7);
    color: var(--ai-muted, #6b7280); border-radius: 9px; padding: 0 6px; margin-left: 4px; }
  .eqb-tab.active .eqb-tab-count { background: #fdeee3; color: var(--ai-orange, #e8670a); }

  .eqb-qtab { display: flex; flex-direction: column; gap: 8px; }
  .eqb-qbar { display: flex; }
  .eqb-editor { height: 220px; border: 1px solid var(--ai-line, #e3e6ec); border-radius: 6px; overflow: hidden; }
  .eqb-sql-fallback { width: 100%; height: 100%; border: none; outline: none; resize: none; padding: 8px;
    font-family: "JetBrains Mono", Consolas, monospace; font-size: 12.75px; }
  .eqb-preview { height: 220px; overflow: auto; }
  .eqb-grid-shell { height: 100%; border: 1px solid var(--ai-line, #e3e6ec); border-radius: 6px; overflow: hidden;
    display: flex; flex-direction: column; background: #fff; }
  .eqb-grid-shell-hdr { flex: 0 0 30px; background: #fafbfc; border-bottom: 1px solid var(--ai-line, #e3e6ec); }
  .eqb-grid-shell-body { flex: 1 1 auto; display: flex; align-items: center; justify-content: center;
    font-size: 12.5px; color: var(--ai-muted, #6b7280); font-style: italic; }
  .eqb-ftbl td.eqb-empty-row { padding: 24px; text-align: center; font-size: 12.5px; color: var(--ai-muted, #6b7280); font-style: italic; }

  .eqb-ftab { min-height: 460px; }
  .eqb-fbar { display: flex; justify-content: flex-end; gap: 8px; margin-top: 8px;
    padding-top: 8px; border-top: 1px solid var(--ai-line, #e3e6ec); }
  .eqb-ftbl-scroll { max-height: 470px; overflow: auto; border: 1px solid var(--ai-line, #e3e6ec); border-radius: 6px; }
  .eqb-ftbl { width: 100%; border-collapse: collapse; font-size: 12.5px; }
  .eqb-ftbl th { position: sticky; top: 0; z-index: 1; background: #fafbfc; text-align: left; font-weight: 700;
    color: var(--ai-ink, #1f2430); padding: 6px 10px; border-bottom: 1px solid var(--ai-line, #e3e6ec); white-space: nowrap; }
  .eqb-ftbl th.eqb-c, .eqb-ftbl td.eqb-c { text-align: center; }
  .eqb-ftbl th.eqb-cbcol, .eqb-ftbl td.eqb-cbcol { width: 34px; padding-left: 8px; padding-right: 8px; }
  .eqb-ftbl td { padding: 2px 10px; border-bottom: 1px solid #f2f3f6; color: var(--ai-ink, #1f2430); }
  .eqb-ftbl tr:hover td { background: var(--ai-row-hover, #f1f3f7); }
  .eqb-fname { font-weight: 600; white-space: nowrap; }
  .eqb-cell { width: 100%; box-sizing: border-box; min-width: 90px; border: 1px solid var(--ai-line, #e3e6ec);
    border-radius: 4px; padding: 2px 6px; font-size: 12.5px; height: 22px; }
  .eqb-cell.eqb-num { width: 64px; min-width: 64px; }
`;
